import { useEffect, useRef } from 'react';
import { WorkerDviDocument } from './workerClient';

/** 前景色 #333 (global.css の --color-fg と揃える) */
const FG = 0x333333ff;
const SP_PER_PT = 65536;
/** ページ個別クロップの余白 (pt)。xtask measure.rs と一致させること */
const CROP_MARGIN_PT = 1;

export interface MetaPage {
  w: number;
  h: number;
  d: number;
}

// meta.formulas.dvi は記事内の複数箇所 (カード・見出し) で共有されるため、
// URL ごとにセッション内で 1 回だけ開く (文書は閉じない)
const docCache = new Map<string, Promise<WorkerDviDocument>>();
function getDoc(url: string): Promise<WorkerDviDocument> {
  let p = docCache.get(url);
  if (!p) {
    p = (async () => {
      const doc = await WorkerDviDocument.open(url);
      await doc.resolveFonts();
      return doc;
    })();
    docCache.set(url, p);
  }
  return p;
}

/** 文字列中のインライン数式 \(...\) の個数 (概要の pageOffset 計算用)。 */
export function countInlineMath(s: string): number {
  return s.split('\\(').length - 1;
}

/**
 * タイトル・概要などのメタ文字列の表示 (Q1/F5)。
 * `\(...\)` の i 番目を meta.formulas.dvi の (pageOffset + i) ページとして
 * インライン描画する。寸法は articles.json 埋め込みの `pages` で即時確定
 * (レイアウトはラスタライズで動かない)。数式が無い文字列はそのまま返す。
 */
export function MathText({
  text,
  formulasUrl,
  pages,
  pageOffset = 0,
}: {
  text: string;
  formulasUrl?: string;
  pages?: MetaPage[] | null;
  pageOffset?: number;
}) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const canvasRefs = useRef(new Map<number, HTMLCanvasElement>());

  // text を テキスト / ページ番号 の列に分割する
  const parts: (string | number)[] = [];
  {
    let rest = text;
    let n = pageOffset;
    for (;;) {
      const open = rest.indexOf('\\(');
      if (open < 0) {
        if (rest) {
          parts.push(rest);
        }
        break;
      }
      if (open > 0) {
        parts.push(rest.slice(0, open));
      }
      const close = rest.indexOf('\\)', open + 2);
      if (close < 0) {
        parts.push(rest.slice(open));
        break;
      }
      parts.push(n++);
      rest = rest.slice(close + 2);
    }
  }
  const mathPages = parts.filter((p): p is number => typeof p === 'number');

  useEffect(() => {
    if (mathPages.length === 0 || !formulasUrl) {
      return;
    }
    let cancelled = false;
    (async () => {
      const doc = await getDoc(formulasUrl);
      if (cancelled) {
        return;
      }
      const fontSizePx = rootRef.current
        ? parseFloat(getComputedStyle(rootRef.current).fontSize) || 16
        : 16;
      const devicePxPerPt = (fontSizePx / 10) * (window.devicePixelRatio || 1);
      for (const [page, canvas] of canvasRefs.current) {
        const key = `${formulasUrl}#${page}@${devicePxPerPt.toFixed(4)}`;
        if (canvas.dataset.rendered === key) {
          continue;
        }
        const image = await doc.renderPage(page, devicePxPerPt, 'page-crop', CROP_MARGIN_PT, FG);
        if (cancelled) {
          return;
        }
        canvas.width = image.width;
        canvas.height = image.height;
        canvas.getContext('2d')?.putImageData(image, 0, 0);
        canvas.dataset.rendered = key;
      }
    })().catch((e: unknown) => {
      console.warn(`meta formulas unavailable: ${formulasUrl}`, e);
    });
    return () => {
      cancelled = true;
    };
    // parts は text から決定的に導出されるので依存は text で足りる
  }, [text, formulasUrl, pageOffset, mathPages.length]);

  if (mathPages.length === 0) {
    return <>{text}</>;
  }
  const emPerSp = 1 / (10 * SP_PER_PT);
  return (
    <span ref={rootRef}>
      {parts.map((part, i) => {
        if (typeof part === 'string') {
          return <span key={i}>{part}</span>;
        }
        const m = pages?.[part];
        const style: React.CSSProperties = m
          ? {
              width: `${m.w * emPerSp}em`,
              height: `${m.h * emPerSp}em`,
              verticalAlign: `${-m.d * emPerSp}em`,
            }
          : { width: '1em', height: '1em' };
        return (
          <canvas
            key={i}
            className="frag-formula frag-inline"
            style={style}
            ref={(el) => {
              if (el) {
                canvasRefs.current.set(part, el);
              } else {
                canvasRefs.current.delete(part);
              }
            }}
          />
        );
      })}
    </span>
  );
}
